/* ============================================================
   Salty Seats - 2026 wholesale application

   One form, four steps. Everything posts at the end, so the steps
   are a reading order rather than four separate submissions - a
   half-finished application is never sent anywhere.

   Trade prices shown here are display only. Whatever charges the
   customer - a B2B catalog, a price list, a wholesale app - is the
   authority on what they actually pay, and this page must never be
   the thing that decides it.
   ============================================================ */
(function () {
  'use strict';

  function money(cents, format) {
    var n = (cents / 100).toFixed(2);
    return (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, n);
  }

  function initApply(root) {
    var form = root.querySelector('[data-ssws-form]');
    if (!form) return;

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-step]'));
    var rail = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-rail]'));
    var headNum = root.querySelector('[data-ssws-stepnum]');
    var headTitle = root.querySelector('[data-ssws-steptitle]');
    var headBlurb = root.querySelector('[data-ssws-stepblurb]');
    var gates = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-gate-input]'));
    var gateNote = root.querySelector('[data-ssws-gate-note]');
    var nextBtns = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-next]'));
    var backBtns = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-back]'));

    var moneyFormat = form.getAttribute('data-money') || '${{amount}}';
    var rate = parseFloat(form.getAttribute('data-rate') || '40') / 100;
    var minUnits = parseInt(form.getAttribute('data-min') || '4', 10);

    var current = 0;

    /* ---------- steps ---------- */

    function gatesOk() {
      return gates.every(function (g) { return g.checked; });
    }

    function show(i) {
      /* The gate lives on step one, so nothing past it opens until
         both agreements are ticked - including the rail, which would
         otherwise be a way straight round it. */
      if (i > 0 && !gatesOk()) i = 0;
      current = Math.max(0, Math.min(steps.length - 1, i));

      steps.forEach(function (s, n) { s.hidden = n !== current; });
      rail.forEach(function (b, n) {
        if (n === current) b.setAttribute('aria-current', 'step');
        else b.removeAttribute('aria-current');
        b.setAttribute('data-state', n < current ? 'done' : n === current ? 'now' : 'todo');
      });

      var active = steps[current];
      if (headNum) headNum.textContent = active.getAttribute('data-num') || '';
      if (headTitle) headTitle.textContent = active.getAttribute('data-title') || '';
      if (headBlurb) headBlurb.textContent = active.getAttribute('data-blurb') || '';

      if (current === steps.length - 1) fillReview();

      var top = root.getBoundingClientRect().top + window.pageYOffset - 20;
      if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
    }

    function refreshGate() {
      var ok = gatesOk();
      nextBtns.forEach(function (b) {
        if (b.getAttribute('data-ssws-next') === 'gated') b.disabled = !ok;
      });
      if (gateNote) gateNote.hidden = ok;
    }

    gates.forEach(function (g) { g.addEventListener('change', refreshGate); });

    rail.forEach(function (b, n) {
      b.addEventListener('click', function () { show(n); });
    });
    nextBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        /* Catch a missing answer on the step it belongs to, rather
           than at the very end when it is four screens away. */
        if (!currentValid()) return;
        show(current + 1);
      });
    });
    backBtns.forEach(function (b) {
      b.addEventListener('click', function () { show(current - 1); });
    });

    /* ---------- single-choice chips ---------- */

    root.querySelectorAll('[data-ssws-chipgroup]').forEach(function (group) {
      var field = group.getAttribute('data-ssws-chipgroup');
      var store = root.querySelector('[data-ssws-chipvalue="' + field + '"]');
      var chips = Array.prototype.slice.call(group.querySelectorAll('[data-ssws-chip]'));
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var value = chip.getAttribute('data-value');
          /* Clicking the chosen one again clears it, so a required
             answer can be undone without reloading the page. */
          var same = store && store.value === value;
          chips.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
          if (!same) chip.setAttribute('aria-pressed', 'true');
          if (store) store.value = same ? '' : value;
        });
      });
    });

    /* ---------- phone ---------- */

    /* Joined to its country code before it goes anywhere, because
       that is the shape a customer profile stores and the only shape
       that can be dialled from another country. A number typed with
       its own + is left alone - the person knew what they were
       doing. */
    var phoneInput = root.querySelector('[data-ssws-phone]');
    var phoneCode = root.querySelector('[data-ssws-phone-code]');

    function normalisePhone() {
      if (!phoneInput) return;
      var raw = (phoneInput.value || '').trim();
      if (!raw) return;
      var digits = raw.replace(/[^0-9]/g, '');
      if (!digits) return;
      if (raw.charAt(0) === '+') {
        phoneInput.value = '+' + digits;
        return;
      }
      /* One leading zero is a national trunk prefix and is dropped
         when the country code goes on. Only one - some numbers begin
         with a meaningful zero after it. */
      digits = digits.replace(/^0/, '');
      var code = phoneCode ? phoneCode.value : '';
      phoneInput.value = code ? code + digits : digits;
    }

    /* The select is invisible, so the flag and code beside it have to
       be kept in step by hand. */
    var phoneFlag = root.querySelector('[data-ssws-phone-flag]');
    var phoneDial = root.querySelector('[data-ssws-phone-dial]');

    function paintPhoneCode() {
      if (!phoneCode) return;
      var opt = phoneCode.options[phoneCode.selectedIndex];
      if (!opt) return;
      if (phoneFlag) phoneFlag.textContent = opt.getAttribute('data-flag') || '';
      if (phoneDial) phoneDial.textContent = '(' + opt.value + ')';
    }

    if (phoneInput) {
      phoneInput.addEventListener('blur', normalisePhone);
    }
    if (phoneCode) {
      phoneCode.addEventListener('change', function () {
        paintPhoneCode();
        /* Re-joining an already-joined number would stack codes, so
           only act once it is back to a plain local number. */
        if (phoneInput && (phoneInput.value || '').trim().charAt(0) !== '+') normalisePhone();
      });
      paintPhoneCode();
    }

    /* ---------- order builder ---------- */

    var qtyInputs = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-qty]'));
    var orderField = root.querySelector('[data-ssws-orderfield]');
    var unitsEls = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-units]'));
    var nextTierEl = root.querySelector('[data-ssws-nexttier]');
    var barEl = root.querySelector('[data-ssws-bar]');
    var tierEls = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-tier]'));
    var linesEl = root.querySelector('[data-ssws-lines]');
    var emptyEl = root.querySelector('[data-ssws-sum-empty]');
    var rrpEl = root.querySelector('[data-ssws-rrp]');
    var savedEl = root.querySelector('[data-ssws-saved]');
    var totalEls = Array.prototype.slice.call(root.querySelectorAll('[data-ssws-total]'));
    var minEl = root.querySelector('[data-ssws-min]');

    function lineData() {
      var out = [];
      qtyInputs.forEach(function (input) {
        /* A disabled input is not posted and must not be counted, so a
           sold-out line cannot reach the summary by any route. */
        if (input.disabled || input.hasAttribute('data-ssws-soldout')) return;
        var n = parseInt(input.value, 10) || 0;
        if (n <= 0) return;
        out.push({
          n: n,
          product: input.getAttribute('data-product') || '',
          colour: input.getAttribute('data-colour') || '',
          rrp: parseInt(input.getAttribute('data-rrp'), 10) || 0,
          variant: input.getAttribute('data-variant') || ''
        });
      });
      return out;
    }

    function recalc() {
      var lines = lineData();
      var units = lines.reduce(function (s, l) { return s + l.n; }, 0);
      var rrpTotal = lines.reduce(function (s, l) { return s + l.n * l.rrp; }, 0);
      var total = Math.round(rrpTotal * (1 - rate));

      unitsEls.forEach(function (el) {
        el.textContent = units + (units === 1 ? ' unit' : ' units');
      });

      /* Tier state. The highest tier the order has not reached is the
         one worth naming; past all of them there is nothing to chase. */
      var next = null;
      tierEls.forEach(function (el) {
        var at = parseInt(el.getAttribute('data-ssws-tier'), 10) || 0;
        var hit = units >= at;
        el.classList.toggle('is-hit', hit);
        if (!hit && next === null) next = { at: at, label: el.getAttribute('data-label') || '' };
      });
      if (nextTierEl) {
        nextTierEl.textContent = next
          ? (next.at - units) + ' more to ' + next.label.toLowerCase()
          : (tierEls.length ? 'Every tier unlocked' : '');
      }

      if (barEl) {
        var top = tierEls.length
          ? parseInt(tierEls[tierEls.length - 1].getAttribute('data-ssws-tier'), 10) || minUnits
          : minUnits;
        barEl.style.width = Math.min(100, Math.round((units / top) * 100)) + '%';
      }

      if (linesEl) {
        linesEl.innerHTML = '';
        lines.forEach(function (l) {
          var row = document.createElement('div');
          row.className = 'ssws-sum__line';
          var left = document.createElement('span');
          left.textContent = l.n + ' × ' + l.product + (l.colour ? ' ' + l.colour : '');
          var right = document.createElement('span');
          right.textContent = money(Math.round(l.n * l.rrp * (1 - rate)), moneyFormat);
          row.appendChild(left);
          row.appendChild(right);
          linesEl.appendChild(row);
        });
        linesEl.hidden = lines.length === 0;
      }
      if (emptyEl) emptyEl.hidden = lines.length > 0;

      if (rrpEl) rrpEl.textContent = money(rrpTotal, moneyFormat);
      if (savedEl) savedEl.textContent = money(rrpTotal - total, moneyFormat);
      totalEls.forEach(function (el) {
        var next = money(total, moneyFormat);
        if (el.textContent !== next) {
          el.textContent = next;
          if (el.classList.contains('ssws-sum__totalv')) replay(el, 'is-bumped');
        }
      });

      if (minEl) {
        minEl.textContent = units >= minUnits
          ? 'Minimum order met'
          : minUnits + ' unit minimum, add ' + (minUnits - units) + ' more';
      }

      /* What actually travels with the application. Built as plain
         text because it has to survive as an email as readily as a
         form field. */
      if (orderField) {
        orderField.value = lines.length
          ? lines.map(function (l) {
              return l.n + ' x ' + l.product + (l.colour ? ' (' + l.colour + ')' : '')
                + ' at ' + money(Math.round(l.rrp * (1 - rate)), moneyFormat) + ' each'
                + ' = ' + money(Math.round(l.n * l.rrp * (1 - rate)), moneyFormat);
            }).join('\n') + '\nTotal at trade: ' + money(total, moneyFormat)
              + ' (' + units + (units === 1 ? ' unit' : ' units') + ')'
          : 'No first order attached';
      }

      root.querySelectorAll('[data-ssws-prod]').forEach(function (prod) {
        var pUnits = 0;
        var pTotal = 0;
        prod.querySelectorAll('[data-ssws-qty]').forEach(function (input) {
          var n = parseInt(input.value, 10) || 0;
          pUnits += n;
          pTotal += n * (parseInt(input.getAttribute('data-rrp'), 10) || 0);
        });
        var sub = prod.querySelector('[data-ssws-prodsub]');
        if (sub) {
          sub.hidden = pUnits === 0;
          sub.textContent = pUnits + (pUnits === 1 ? ' unit' : ' units')
            + ' · ' + money(Math.round(pTotal * (1 - rate)), moneyFormat);
        }
      });

      qtyInputs.forEach(function (input) {
        var card = input.closest('.ssws-col');
        if (card) card.classList.toggle('is-picked', (parseInt(input.value, 10) || 0) > 0);
      });
    }

    /* Restarting an animation needs the class gone for a frame, or the
       browser treats it as still running and nothing happens on a
       second click. */
    function replay(el, cls) {
      if (!el) return;
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
      setTimeout(function () { el.classList.remove(cls); }, 600);
    }

    root.querySelectorAll('[data-ssws-step-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var wrap = btn.closest('.ssws-step__qty');
        var input = wrap && wrap.querySelector('[data-ssws-qty]');
        if (!input || input.disabled) return;
        var d = btn.getAttribute('data-ssws-step-btn') === 'up' ? 1 : -1;
        var before = parseInt(input.value, 10) || 0;
        var after = Math.max(0, before + d);
        if (after === before) return;
        input.value = after;
        replay(input, 'is-bumped');
        replay(input.closest('.ssws-col'), 'is-bumped');
        recalc();
      });
    });

    qtyInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        var n = parseInt(input.value, 10);
        if (isNaN(n) || n < 0) input.value = 0;
        recalc();
      });
    });

    /* ---------- validation ---------- */

    /* Required fields on a hidden step still block submission, and the
       browser cannot focus what it cannot see - it gives up with an
       unfocusable-control error and the form silently does nothing.
       So whenever validation fails, open the step the offending field
       lives on before asking the browser to report it. */
    function fieldsIn(step) {
      return Array.prototype.slice.call(step.querySelectorAll('input, select, textarea'));
    }

    function revealInvalid() {
      for (var i = 0; i < steps.length; i++) {
        var bad = fieldsIn(steps[i]).filter(function (el) {
          return typeof el.checkValidity === 'function' && !el.checkValidity();
        })[0];
        if (bad) {
          show(i);
          bad.reportValidity();
          return false;
        }
      }
      return true;
    }

    function currentValid() {
      var bad = fieldsIn(steps[current]).filter(function (el) {
        return typeof el.checkValidity === 'function' && !el.checkValidity();
      })[0];
      if (!bad) return true;
      bad.reportValidity();
      return false;
    }

    /* ---------- review ---------- */

    function fillReview() {
      root.querySelectorAll('[data-ssws-echo]').forEach(function (el) {
        var name = el.getAttribute('data-ssws-echo');
        var src = form.querySelector('[name="' + name + '"]');
        var val = src ? (src.value || '').trim() : '';
        el.textContent = val || el.getAttribute('data-empty') || 'Not answered yet';
      });
    }

    /* ---------- submit ---------- */

    function showThanks(which) {
      var panel = root.querySelector(which || '[data-ssws-thanks]');
      var railWrap = root.querySelector('.ssws-rail');
      steps.forEach(function (s) { s.hidden = true; });
      var head = root.querySelector('.ssws-panel__head');
      if (head) head.hidden = true;
      if (railWrap) railWrap.hidden = true;
      if (panel) {
        panel.hidden = false;
        panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
      }
      var top = root.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }


    form.addEventListener('submit', function (e) {
      /* Only the review step submits. Enter pressed in any earlier
         field would otherwise fire off a half-filled application. */
      if (current !== steps.length - 1) {
        e.preventDefault();
        if (currentValid()) show(current + 1);
        return;
      }
      if (!gatesOk()) {
        e.preventDefault();
        show(0);
        return;
      }
      normalisePhone();
      if (!revealInvalid()) {
        e.preventDefault();
        return;
      }

      /* Post it in the background so the thanks panel replaces the
         form in place. Anything unexpected - a network drop, a
         redirect we cannot read, an error page - falls through to a
         normal submit, because losing a finished application is far
         worse than losing the transition. */
      if (!window.fetch || !window.FormData) return;
      e.preventDefault();

      var submitBtn = form.querySelector('[type="submit"]');
      var wasLabel = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = form.getAttribute('data-sending') || 'Sending';
      }

      function restore() {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = wasLabel;
        }
      }

      /* The email trail, sent either way. It is what a person reads;
         the endpoint is what the store records. Fired without waiting
         so a slow contact post never holds up the payment step. */
      function postEmailCopy() {
        return fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html' }
        })
          .then(function (r) {
            if (!r.ok) throw new Error('post failed');
            return r.text();
          })
          .then(function (html) {
            /* Shopify answers a good contact post by redirecting back
               with contact_posted=true. Read the page it landed on
               rather than trusting the status alone. */
            if (!/contact_posted=true/.test(html) && !/ssws-thanks/.test(html)) {
              throw new Error('not posted');
            }
          });
      }

      var endpoint = (form.getAttribute('data-endpoint') || '').trim();

      if (!endpoint) {
        postEmailCopy().then(showThanks).catch(function () {
          restore();
          form.submit();
        });
        return;
      }

      /* With an endpoint set, the application creates the customer,
         the company and the first order, and hands back a checkout.
         If any of that fails we do not strand the stockist: the plain
         contact post still runs and they still get a thanks panel, so
         the worst case is the application we had before this existed. */
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload())
      })
        .then(function (r) {
          return r.json().catch(function () { return { ok: false }; });
        })
        .then(function (data) {
          if (!data || !data.ok) throw new Error((data && data.error) || 'endpoint failed');
          postEmailCopy().catch(function () {});

          var payNow = form.getAttribute('data-pay-now') === 'true';
          var link = root.querySelector('[data-ssws-pay-link]');
          if (payNow && data.checkoutUrl && link) {
            link.href = data.checkoutUrl;
            showThanks('[data-ssws-pay]');
            /* Sent, then followed. They see what happened before the
               page changes under them, and a blocked redirect leaves
               a button rather than nothing. */
            window.setTimeout(function () {
              window.location.assign(data.checkoutUrl);
            }, 1200);
            return;
          }
          showThanks();
        })
        .catch(function () {
          postEmailCopy().then(showThanks).catch(function () {
            restore();
            form.submit();
          });
        });
    });

    /* What the endpoint receives. Fields are read by their map name
       rather than their posted label, so rewording a label on the form
       cannot quietly change the shape of the payload. */
    function payload() {
      function val(name) {
        var el = form.querySelector('[data-ssws-map="' + name + '"]');
        if (!el) return '';
        if (el.type === 'checkbox') return el.checked;
        return (el.value || '').trim();
      }

      var lines = lineData()
        .filter(function (l) { return l.variant; })
        .map(function (l) {
          return {
            variantId: l.variant,
            quantity: l.n,
            title: l.product + (l.colour ? ' ' + l.colour : '')
          };
        });

      var hp = form.querySelector('[data-ssws-hp]');

      return {
        contact: {
          firstName: val('firstName'),
          lastName: val('lastName'),
          email: val('email'),
          phone: val('phone')
        },
        business: {
          name: val('businessName'),
          type: val('businessType'),
          abn: val('abn'),
          website: val('website'),
          sellWhere: val('sellWhere'),
          heard: val('heard'),
          tradeUpdates: val('tradeUpdates') === true
        },
        address: {
          street: val('street'),
          city: val('city'),
          state: val('state'),
          postcode: val('postcode'),
          country: val('country')
        },
        agreed: gatesOk(),
        order: {
          rate: rate * 100,
          lines: lines,
          summary: orderField ? orderField.value : ''
        },
        hp: hp ? hp.value : '',
        submittedAt: new Date().toISOString()
      };
    }

    refreshGate();
    recalc();
    show(0);
  }

  function boot() {
    document.querySelectorAll('.ssws').forEach(function (root) {
      if (root.dataset.sswsInit === '1') return;
      root.dataset.sswsInit = '1';
      if (root.querySelector('[data-ssws-form]')) initApply(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
